import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Users, Coins, History as HistoryIcon, Send, ShieldCheck, Copy, Check } from "lucide-react";

export default function GameArena() {
  const [state, setState] = useState(null);
  const [wallet, setWallet] = useState({ balance_usdt: 0 });
  const [amount, setAmount] = useState("5");
  const [auto, setAuto] = useState("2.00");  const [betting, setBetting] = useState(false);
  const [cashing, setCashing] = useState(false);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyRound, setVerifyRound] = useState(null);
  const esRef = useRef(null);
  const tickerRef = useRef(null);
  const { user } = useAuth();

  const refreshWallet = async () => {
    try {
      const { data } = await api.get("/wallet");
      setWallet(data);
    } catch (err) {
      console.warn("wallet refresh failed:", err?.message);
    }
  };

  // Smooth multiplier ticker (interpolates between SSE ticks for fluid animation)
  const lastTickRef = useRef({ at: 0, mult: 1.0, status: "betting" });

  // SSE connection + wallet polling: setup once on mount, cleanup on unmount.
  useEffect(() => {
    refreshWallet();
    const wInt = setInterval(refreshWallet, 5000);

    const token = localStorage.getItem("crash_token");
    const url = `${API}/game/stream${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    const applySnapshot = (snap) => {
      setState(snap);
      lastTickRef.current = {
        at: performance.now(),
        mult: snap.current_multiplier,
        status: snap.status,
      };
    };

    es.addEventListener("snapshot", (e) => applySnapshot(JSON.parse(e.data)));
    es.addEventListener("round.new", (e) => applySnapshot(JSON.parse(e.data)));
    es.addEventListener("round.start", (e) => applySnapshot(JSON.parse(e.data)));
    es.addEventListener("round.crashed", (e) => applySnapshot(JSON.parse(e.data)));
    es.addEventListener("tick", (e) => {
      const d = JSON.parse(e.data);
      lastTickRef.current = { at: performance.now(), mult: d.multiplier, status: "running" };
      setState((s) => s ? { ...s, current_multiplier: d.multiplier, status: "running" } : s);
    });
    es.addEventListener("cashout", (e) => {
      const d = JSON.parse(e.data);
      toast.success(`${d.name} cashed out @ ${d.multiplier}× (+${d.win} USDT)`);
      refreshWallet();
    });
    es.addEventListener("bet", () => {
      // Update player list via next snapshot or tick
    });
    es.addEventListener("chat", (e) => {
      const m = JSON.parse(e.data);
      setChat((prev) => prev.some((x) => x.msg_id === m.msg_id) ? prev : [...prev.slice(-49), m]);
    });
    es.onerror = () => { /* auto-reconnect built into EventSource */ };

    // Local smoothing animation (60fps interpolation between ticks)
    tickerRef.current = setInterval(() => {
      const { at, mult, status } = lastTickRef.current;
      if (status !== "running") return;
      const elapsed = (performance.now() - at) / 1000;
      const smoothed = +(mult * Math.pow(1.06, elapsed)).toFixed(2);
      setState((s) => (s && s.status === "running" ? { ...s, current_multiplier: smoothed } : s));
    }, 60);

    // Chat history is delivered by SSE on connect (server replays last 30 chat events) — no extra REST fetch needed.

    return () => {
      es.close();
      clearInterval(wInt);
      clearInterval(tickerRef.current);
    };
  }, []);

  const placeBet = async () => {
    setBetting(true);
    try {
      await api.post("/game/bet", {
        amount: parseFloat(amount),
        auto_cashout: auto ? parseFloat(auto) : null,
      });
      toast.success("You're in!");
      refreshWallet();
    } catch (err) {
      toast.error(apiError(err, "Failed to join"));
    } finally {
      setBetting(false);
    }
  };

  const cashout = async () => {
    setCashing(true);
    try {
      const { data } = await api.post("/game/cashout");
      toast.success(`Cashed out at ${data.me?.cashed_at}× — won ${data.me?.win} USDT`);
      refreshWallet();
    } catch (err) {
      toast.error(apiError(err, "Cashout failed"));
    } finally {
      setCashing(false);
    }
  };

  const sendChat = async (e) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text) return;
    setChatInput("");
    try {
      await api.post("/chat/send", { text });
    } catch (err) {
      toast.error(apiError(err, "Chat failed"));
    }
  };

  const openVerify = async (round_id) => {
    try {
      const { data } = await api.get(`/game/verify/${round_id}`);
      setVerifyRound(data);
      setVerifyOpen(true);
    } catch {
      toast.error("Could not verify round");
    }
  };

  if (!state) return <div className="min-h-[calc(100vh-4rem)] grid place-items-center text-neon-green font-mono">CONNECTING TO GRID…</div>;

  const inRound = !!state.me;
  const canCashout = state.status === "running" && inRound && !state.me?.cashed_at;
  const canBet = state.status === "betting" && !inRound;

  return (
    <div className="min-h-[calc(100vh-4rem)] relative scanlines" data-testid="game-arena">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6 relative z-10">
        {/* Left: Curve + status + provably-fair + chat */}
        <div className="lg:col-span-3 space-y-4">
          <CurvePanel state={state} />
          <HowToPlay />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProvablyFairCard state={state} onVerify={openVerify} />
            <HistoryStrip history={state.history} onVerify={openVerify} />
          </div>
          <ChatPanel chat={chat} onSend={sendChat} input={chatInput} setInput={setChatInput} disabled={!user} />
        </div>

        {/* Right: Bet panel + players */}
        <aside className="space-y-4">
          <div className="panel p-5" data-testid="wallet-mini">
            <div className="label-mono">Wallet</div>
            <div className="font-mono text-2xl font-bold mt-1 text-neon-green glow-green" data-testid="wallet-balance-display">
              {wallet.balance_usdt?.toFixed(2)} <span className="text-xs text-white/50">USDT</span>
            </div>
          </div>

          <div className="panel p-5 space-y-4" data-testid="bet-panel">
            <div className="flex items-center justify-between">
              <div className="label-mono">Entry Panel</div>
              <PhaseBadge status={state.status} remaining={state.phase_remaining} />
            </div>

            <div>
              <label className="label-mono">Amount (USDT) · min 5</label>
              <input
                className="field mt-2"
                type="number" min="5" step="0.5"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={!canBet}
                data-testid="bet-amount-input"
              />
              <div className="grid grid-cols-4 gap-1.5 mt-2">
                {[5, 10, 25, 50, 100, 200, 500].map((v) => (
                  <button key={v} className="panel py-1.5 text-xs font-mono hover:border-neon-green transition-colors disabled:opacity-30" disabled={!canBet} onClick={() => setAmount(String(v))} data-testid={`quick-bet-${v}`}>{v}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="label-mono">Auto Cashout</label>
              <input
                className="field mt-2"
                type="number" step="0.01" min="1.01"
                value={auto}
                onChange={(e) => setAuto(e.target.value)}
                placeholder="2.00"
                disabled={!canBet}
                data-testid="auto-cashout-input"
              />
            </div>

            {canCashout ? (
              <motion.button
                onClick={cashout}
                disabled={cashing}
                className="w-full bg-neon-green text-black font-display font-black uppercase tracking-wider py-5 text-lg shadow-neon-green-lg hover:bg-white transition-colors"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ repeat: Infinity, duration: 0.8 }}
                data-testid="cashout-button"
              >
                CASH OUT @ {state.current_multiplier?.toFixed(2)}×
              </motion.button>
            ) : (
              <button
                onClick={placeBet}
                disabled={!canBet || betting}
                className="btn-neon w-full !py-4 disabled:opacity-30"
                data-testid="place-bet-button"
              >
                {inRound ? "ENTRY LOCKED" : state.status === "betting" ? `Join Round (${state.phase_remaining}s)` : "WAITING…"}
              </button>
            )}

            {inRound && (
              <div className="text-xs font-mono text-white/60 pt-2 border-t border-neon-line">
                YOUR ENTRY: <span className="text-white">{state.me.bet} USDT</span>
                {state.me.auto_cashout && <> · AUTO @ {state.me.auto_cashout}×</>}
                {state.me.cashed_at && <div className="text-neon-green mt-1">Cashed @ {state.me.cashed_at}× · +{state.me.win} USDT</div>}
              </div>
            )}
          </div>

          <PlayersPanel players={state.players} pot={state.total_pot} />
        </aside>
      </div>

      {verifyOpen && <VerifyModal data={verifyRound} onClose={() => setVerifyOpen(false)} />}
    </div>
  );
}

function CurvePanel({ state }) {
  const m = state.current_multiplier;
  const crashed = state.status === "crashed";
  const running = state.status === "running";

  const points = [];
  const W = 600, H = 280;
  if (running || crashed) {
    const target = Math.min(m, 50);
    const steps = 60;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const mult = 1 + (target - 1) * t;
      const x = t * W;
      const norm = Math.min((mult - 1) / 9, 1);
      const y = H - 20 - Math.pow(norm, 0.7) * (H - 40);
      points.push(`${x},${y}`);
    }
  }

  return (
    <div className="panel p-6 lg:p-8 relative overflow-hidden min-h-[400px]" data-testid="curve-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="label-mono flex items-center gap-2">
          <span className="dot-live" /> ROUND <span className="text-white font-mono">{state.round_id?.slice(2, 8)}</span>
        </div>
        <div className="label-mono">{state.player_count} PLAYERS · {state.total_pot.toFixed(2)} USDT POT</div>
      </div>

      <div className="relative flex flex-col items-center justify-center min-h-[280px]">
        <motion.div
          key={state.status}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          {state.status === "betting" && (
            <>
              <div className="label-mono">Next round in</div>
              <div className="font-display font-black text-7xl lg:text-8xl text-neon-cyan glow-cyan mt-2 font-mono" data-testid="phase-countdown">
                {state.phase_remaining.toFixed(1)}s
              </div>
              <div className="label-mono mt-3">Join the round</div>
            </>
          )}
          {running && (
            <>
              <div className="label-mono">Multiplier</div>
              <div className="font-display font-black text-7xl lg:text-9xl text-neon-green glow-green font-mono leading-none" data-testid="crash-multiplier-value">
                {m.toFixed(2)}×
              </div>
              <div className="label-mono mt-3">Cash out before crash</div>
            </>
          )}
          {crashed && (
            <>
              <div className="label-mono text-neon-red">CRASHED AT</div>
              <motion.div
                animate={{ x: [-3, 3, -2, 2, 0], opacity: [1, 0.7, 1] }}
                transition={{ duration: 0.4 }}
                className="font-display font-black text-7xl lg:text-9xl text-neon-red glow-red font-mono leading-none"
                data-testid="crash-multiplier-value"
              >
                {m.toFixed(2)}×
              </motion.div>
              <div className="label-mono mt-3 text-neon-red">round over</div>
            </>
          )}
        </motion.div>

        {(running || crashed) && points.length > 0 && (
          <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-x-0 bottom-0 w-full opacity-90" preserveAspectRatio="none" data-testid="crash-curve-svg">
            <defs>
              <linearGradient id="curveGrad" x1="0" x2="1" y1="1" y2="0">
                <stop offset="0%" stopColor={crashed ? "#FF003C" : "#39FF14"} stopOpacity="0.05" />
                <stop offset="100%" stopColor={crashed ? "#FF003C" : "#39FF14"} stopOpacity="0.8" />
              </linearGradient>
            </defs>
            <polyline
              points={points.join(" ")}
              fill="none"
              stroke={crashed ? "#FF003C" : "#39FF14"}
              strokeWidth="3"
              style={{ filter: `drop-shadow(0 0 8px ${crashed ? "#FF003C" : "#39FF14"})` }}
            />
            <polygon
              points={`0,${H} ${points.join(" ")} ${W},${H}`}
              fill="url(#curveGrad)"
            />
          </svg>
        )}
      </div>
    </div>
  );
}

function PhaseBadge({ status, remaining }) {
  const map = {
    betting: { c: "text-neon-cyan border-neon-cyan", t: `JOIN ${remaining.toFixed(1)}s` },
    running: { c: "text-neon-green border-neon-green", t: "RUNNING" },
    crashed: { c: "text-neon-red border-neon-red", t: "CRASHED" },
  };
  const m = map[status] || map.betting;
  return <div className={`label-mono border px-2 py-1 ${m.c}`} data-testid="phase-badge">{m.t}</div>;
}

function PlayersPanel({ players, pot }) {
  return (
    <div className="panel p-5" data-testid="players-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="label-mono flex items-center gap-2"><Users className="w-3 h-3" /> Players</div>
        <div className="label-mono flex items-center gap-2"><Coins className="w-3 h-3 text-neon-green" /> {pot.toFixed(2)}</div>
      </div>
      <div className="space-y-1.5 max-h-60 overflow-y-auto">
        {players.length === 0 && <div className="text-white/40 text-xs font-mono">No bets yet…</div>}
        {players.map((p, i) => (
          <div key={`${p.name}-${p.bet}-${i}`} className="flex items-center justify-between text-xs font-mono py-1.5 border-b border-neon-line/50 last:border-0" data-testid={`player-row-${i}`}>
            <span className="text-white/80 truncate max-w-[40%]">{p.name}</span>
            <span className="text-white/60">{p.bet}</span>
            {p.cashed_at ? (
              <span className="text-neon-green">{p.cashed_at}× · +{p.win}</span>
            ) : (
              <span className="text-white/40">…</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryStrip({ history, onVerify }) {
  return (
    <div className="panel p-4" data-testid="history-strip">
      <div className="flex items-center gap-2 mb-3">
        <HistoryIcon className="w-3 h-3 text-white/60" />
        <div className="label-mono">Recent Crashes · Click to verify</div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {history.length === 0 && <div className="text-white/40 text-xs font-mono">no rounds yet</div>}
        {history.map((h, i) => {
          const danger = h.crash_point < 1.5;
          const big = h.crash_point >= 5;
          return (
            <button
              key={h.round_id}
              onClick={() => onVerify(h.round_id)}
              className={`font-mono text-xs px-2.5 py-1 border transition-colors hover:bg-white/5 ${
                big ? "border-neon-green text-neon-green shadow-neon-green" :
                danger ? "border-neon-red/60 text-neon-red" :
                "border-neon-line text-white/70"
              }`}
              data-testid={`history-pill-${i}`}
            >
              {h.crash_point.toFixed(2)}×
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ProvablyFairCard({ state, onVerify }) {
  const [copied, setCopied] = useState(false);
  const hash = state.server_seed_hash || "";
  const revealed = state.revealed_seed;
  const isCrashed = state.status === "crashed";
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="panel p-4" data-testid="provably-fair-card">
      <div className="flex items-center justify-between mb-3">
        <div className="label-mono flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-neon-green" /> Provably Fair</div>
        {isCrashed && (
          <button onClick={() => onVerify(state.round_id)} className="label-mono text-neon-green hover:underline" data-testid="verify-current-button">verify →</button>
        )}
      </div>
      <div className="space-y-2 text-xs font-mono">
        <div className="group relative">
          <div className="text-white/40">Server Seed Hash (committed)</div>
          <div className="flex items-center gap-2">
            <code className="text-neon-cyan break-all flex-1 cursor-help" title={hash || "—"} data-testid="server-seed-hash">
              {hash ? `${hash.slice(0, 24)}…${hash.slice(-8)}` : "—"}
            </code>
            {hash && <button onClick={() => copy(hash)} className="text-white/60 hover:text-neon-green" data-testid="copy-hash">{copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}</button>}
          </div>
          {hash && (
            <div className="absolute left-0 right-0 top-full mt-1 z-30 hidden group-hover:block pointer-events-none">
              <div className="panel !border-neon-cyan/60 p-2 bg-black/95 shadow-neon-cyan">
                <code className="text-[10px] text-neon-cyan break-all leading-relaxed" data-testid="server-seed-hash-tooltip">{hash}</code>
              </div>
            </div>
          )}
        </div>
        <div className="group relative">
          <div className="text-white/40">Server Seed (revealed after crash)</div>
          <code className={`break-all cursor-help ${revealed ? "text-neon-green" : "text-white/30"}`} title={revealed || "locked"} data-testid="server-seed-revealed">
            {revealed ? `${revealed.slice(0, 24)}…${revealed.slice(-8)}` : "locked · revealed at crash"}
          </code>
          {revealed && (
            <div className="absolute left-0 right-0 top-full mt-1 z-30 hidden group-hover:block pointer-events-none">
              <div className="panel !border-neon-green/60 p-2 bg-black/95 shadow-neon-green">
                <code className="text-[10px] text-neon-green break-all leading-relaxed">{revealed}</code>
              </div>
            </div>
          )}
        </div>
        <div className="text-white/30 text-[10px] leading-relaxed pt-1 border-t border-neon-line/40">
          SHA-256(server_seed) is committed before any bets. After crash, the seed is revealed — you can verify the crash point was derived deterministically.
        </div>
      </div>
    </div>
  );
}

function ChatPanel({ chat, onSend, input, setInput, disabled }) {
  const scrollerRef = useRef(null);
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [chat]);
  return (
    <div className="panel p-4" data-testid="chat-panel">
      <div className="label-mono mb-3">// arena_chat · {chat.length} msgs</div>
      <div ref={scrollerRef} className="h-48 overflow-y-auto space-y-1 text-xs font-mono pr-2 mb-3" data-testid="chat-messages">
        {chat.length === 0 && <div className="text-white/40">no messages yet — break the silence</div>}
        {chat.map((m) => (
          <div key={m.msg_id} className="flex gap-2" data-testid={`chat-msg-${m.msg_id}`}>
            <span className="text-neon-green shrink-0">{m.name}:</span>
            <span className="text-white/80 break-words">{m.text}</span>
          </div>
        ))}
      </div>
      <form onSubmit={onSend} className="flex gap-2">
        <input
          className="field !py-2 flex-1"
          placeholder={disabled ? "log in to chat" : "type a message…"}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={200}
          disabled={disabled}
          data-testid="chat-input"
        />
        <button type="submit" disabled={disabled || !input.trim()} className="btn-neon !py-2 !px-3 disabled:opacity-30" data-testid="chat-send-button">
          <Send className="w-3.5 h-3.5" />
        </button>
      </form>
    </div>
  );
}

function VerifyModal({ data, onClose }) {
  if (!data) return null;
  const ok = data.hash_match && data.crash_match;
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose} data-testid="verify-modal">
      <div className="panel max-w-xl w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="label-mono flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-neon-green" /> Verify Round {data.round_id.slice(2, 8)}</div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="verify-close">✕</button>
        </div>
        <div className="space-y-3 text-xs font-mono">
          <Row k="server_seed" v={data.server_seed} />
          <Row k="server_seed_hash (stored)" v={data.server_seed_hash_stored} />
          <Row k="server_seed_hash (computed)" v={data.server_seed_hash_computed} highlight={data.hash_match ? "ok" : "bad"} />
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><div className="text-white/40">Crash (stored)</div><div className="text-neon-green text-lg">{data.crash_point_stored}×</div></div>
            <div><div className="text-white/40">Crash (computed)</div><div className={`text-lg ${data.crash_match ? "text-neon-green" : "text-neon-red"}`}>{data.crash_point_computed}×</div></div>
          </div>
          <div className={`p-3 border ${ok ? "border-neon-green text-neon-green" : "border-neon-red text-neon-red"} mt-2`} data-testid="verify-result">
            {ok ? "✓ FAIR — Hashes & crash point match" : "✗ MISMATCH — Audit failed"}
          </div>
          <div className="text-[10px] text-white/40 leading-relaxed">
            Formula: r = int(SHA-256(seed)[0:13], 16) / 2^52; if r &lt; 0.03 → crash = 1.00×; else crash = 0.97 / (1-r)
          </div>
        </div>
      </div>
    </div>
  );
}

const HOW_TO_STEPS = [
  { n: "01", title: "Fund your wallet", desc: "Deposit USDT from the Wallet page. Your balance shows on the right.", color: "text-neon-cyan border-neon-cyan/40" },
  { n: "02", title: "Set your entry", desc: "Pick an amount (min 5 USDT) and an optional Auto Cashout multiplier.", color: "text-neon-green border-neon-green/40" },
  { n: "03", title: "Join the round", desc: "Hit Join Round during the countdown. Entries lock when the round starts.", color: "text-neon-cyan border-neon-cyan/40" },
  { n: "04", title: "Cash out in time", desc: "The multiplier climbs from 1.00×. Cash out before it crashes to win entry × multiplier. Too late = round lost.", color: "text-neon-red border-neon-red/40" },
];

function HowToPlay() {
  return (
    <div className="panel p-5" data-testid="how-to-play-card">
      <div className="label-mono mb-4">// how_to_play</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {HOW_TO_STEPS.map((s) => (
          <div key={s.n} className={`border ${s.color.split(" ")[1]} bg-black/40 p-4`} data-testid={`how-to-step-${s.n}`}>
            <div className={`font-mono text-xs font-bold ${s.color.split(" ")[0]}`}>{s.n}</div>
            <div className="font-display font-bold text-sm uppercase tracking-wide mt-2">{s.title}</div>
            <p className="text-white/50 text-xs mt-1.5 leading-relaxed font-body">{s.desc}</p>
          </div>
        ))}
      </div>
      <p className="text-white/40 text-xs font-mono mt-4">Tip: Auto Cashout secures your win automatically at your target multiplier — even if you blink.</p>
    </div>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div>
      <div className="text-white/40">{k}</div>
      <code className={`break-all ${highlight === "ok" ? "text-neon-green" : highlight === "bad" ? "text-neon-red" : "text-white"}`}>{v}</code>
    </div>
  );
}
